<template>
  <header class="site-header" :class="{ 'is-scrolled': isScrolled, 'is-menu-open': mobileMenuOpen }" data-build="site-header-v6">
    <div class="wrapper header-tools">
      <button type="button" class="tool-btn language-switch" :aria-label="$t('languageSwitchAria')" @click="langChange">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="8.8"></circle>
          <path d="M3.2 12h17.6M12 3.2c2.9 2.4 4.5 5.3 4.5 8.8S14.9 18.4 12 20.8M12 3.2C9.1 5.6 7.5 8.5 7.5 12s1.6 6.4 4.5 8.8"></path>
        </svg>
        <span>{{ languageLabel }}</span>
      </button>

      <button type="button" class="tool-btn" @click="openMainSystem">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7.2 6.1c0-1.9 1.6-3.5 3.6-3.5h2.4c2 0 3.6 1.6 3.6 3.5v3.2c0 1.4-.8 2.7-2 3.3l4 7h-2.6l-3.6-6.4h-1.4l-3.6 6.4H5.1l4-7c-1.2-.6-1.9-1.9-1.9-3.3V6.1z"></path>
          <path d="M10 9.2h4"></path>
        </svg>
        <span>{{ $t('headerScoringService') }}</span>
      </button>
    </div>

    <div class="header-nav-row">
      <div class="nav-lead"></div>
      <a class="brand" @click="toPath('/index')">
        <img src="../../assets/logo/logo-cn.png" alt="华晟育林" />
      </a>

      <nav class="main-nav" :class="{ open: mobileMenuOpen }">
        <div
          v-for="item in menu"
          :key="item.path"
          class="nav-entry"
          :class="{ 'has-children': item.children && item.children.length }"
        >
          <button
            type="button"
            class="nav-item"
            :class="{ active: isActive(item.path) }"
            @click="toPath(item.path)"
          >
            {{ item.name }}
          </button>
          <div v-if="item.children && item.children.length" class="nav-submenu">
            <a
              v-for="child in item.children"
              :key="child.href"
              class="nav-subitem"
              :href="child.href"
              @click="closeMobileMenu"
            >
              {{ child.name }}
            </a>
          </div>
        </div>
      </nav>

      <div class="mobile-actions">
        <button type="button" class="mobile-icon-btn language-switch" :aria-label="$t('languageSwitchAria')" @click="langChange">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="8.8"></circle>
            <path d="M3.2 12h17.6M12 3.2c2.9 2.4 4.5 5.3 4.5 8.8S14.9 18.4 12 20.8M12 3.2C9.1 5.6 7.5 8.5 7.5 12s1.6 6.4 4.5 8.8"></path>
          </svg>
        </button>
        <button type="button" class="mobile-icon-btn" :aria-label="$t('headerScoringService')" @click="openMainSystem">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7.2 6.1c0-1.9 1.6-3.5 3.6-3.5h2.4c2 0 3.6 1.6 3.6 3.5v3.2c0 1.4-.8 2.7-2 3.3l4 7h-2.6l-3.6-6.4h-1.4l-3.6 6.4H5.1l4-7c-1.2-.6-1.9-1.9-1.9-3.3V6.1z"></path>
            <path d="M10 9.2h4"></path>
          </svg>
        </button>
        <button type="button" class="mobile-menu-btn" :aria-expanded="String(mobileMenuOpen)" @click="toggleMobileMenu">
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </div>

    <transition name="mobile-mask-fade">
      <button v-if="mobileMenuOpen" type="button" class="mobile-nav-mask" aria-label="Close menu" @click="closeMobileMenu"></button>
    </transition>
  </header>
</template>

<script>
import { buildScaleLink, loadWebsiteCatalog } from '@/request/apis/websiteCatalog'

export default {
  data() {
    return {
      lang: localStorage.getItem('locale') || 'zh',
      isScrolled: false,
      mobileMenuOpen: false,
      productScales: []
    }
  },
  computed: {
    languageLabel() {
      return this.lang === 'zh' ? this.$t('headerLangZh') : this.$t('headerLangEn')
    },
    menu() {
      return [
        { name: this.$t('headerMenuHome'), path: '/index' },
        { name: this.$t('headerMenuTrain'), path: '/train' },
        {
          name: this.$t('headerMenuProduct'),
          path: '/product',
          children: this.productMenuChildren
        },
        { name: this.$t('headerMenuConsult'), path: '/consult' },
        { name: this.$t('headerMenuNews'), path: '/news' },
        {
          name: this.$t('headerMenuAboutYulin'),
          path: '/about',
          children: [
            { name: this.$t('headerMenuSocial'), href: '#/about/other?type=3' }
          ]
        }
      ]
    },
    productMenuChildren() {
      return this.productScales
        .filter(item => item.navVisible)
        .sort((a, b) => {
          const sortDelta = Number(a.navSort || 0) - Number(b.navSort || 0)
          if (sortDelta !== 0) return sortDelta
          return Number(a.sortNum || 0) - Number(b.sortNum || 0)
        })
        .map(item => ({
          name: item.shortName || item.name || item.code,
          href: buildScaleLink(item)
        }))
    }
  },
  watch: {
    '$route.path'() {
      this.closeMobileMenu()
    }
  },
  mounted() {
    this.handleScroll()
    this.loadProductMenu()
    window.addEventListener('scroll', this.handleScroll, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.pageYOffset > 10
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false
    },
    loadProductMenu() {
      loadWebsiteCatalog(this.lang).then(list => {
        this.productScales = Array.isArray(list) ? list : []
      }).catch(() => {
        this.productScales = []
      })
    },
    isActive(path) {
      if (path === '/index') return this.$route.path === '/' || this.$route.path === '/index'
      if (path === '/about') return this.$route.path.indexOf('/about') === 0
      if (path === '/product') return this.$route.path.indexOf('/product') === 0
      if (path === '/train') return this.$route.path.indexOf('/train') === 0
      if (path === '/consult') return this.$route.path.indexOf('/consult') === 0
      if (path === '/news') return this.$route.path.indexOf('/news') === 0
      return this.$route.path === path
    },
    toPath(path) {
      this.closeMobileMenu()
      this.$router.push({ path })
    },
    openMainSystem() {
      window.open('https://www.hsyl-tech.com/scaleClient/', '_blank')
    },
    langChange() {
      this.lang = this.lang === 'zh' ? 'en' : 'zh'
      this.$i18n.locale = this.lang
      localStorage.setItem('locale', this.lang)
      document.documentElement.lang = this.lang === 'en' ? 'en' : 'zh-CN'
      window.location.reload()
    }
  }
}
</script>

<style scoped>
.site-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 30;
  height: 84px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: saturate(140%) blur(10px);
  box-shadow: 0 0 0 rgba(12, 42, 48, 0);
  transition: background 0.24s ease, box-shadow 0.24s ease;
}

.site-header.is-scrolled,
.site-header.is-menu-open {
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 10px 30px rgba(12, 42, 48, 0.08);
}

.site-header .wrapper {
  width: var(--site-container);
  max-width: calc(100% - var(--site-gutter));
}

.header-tools {
  height: 43px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 34px;
}

.tool-btn {
  border: 0;
  background: transparent;
  color: #01898f;
  font-size: 15px;
  line-height: 1;
  font-family: var(--font-nav);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 7px 12px;
  border-radius: 999px;
  transition: background-color 0.22s cubic-bezier(0.2, 0.8, 0.2, 1),
    color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.tool-btn:hover,
.tool-btn:focus-visible {
  background: rgba(1, 137, 143, 0.1);
  color: #01777d;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(1, 137, 143, 0.14);
}

.tool-btn:focus-visible {
  outline: 2px solid rgba(1, 137, 143, 0.38);
  outline-offset: 2px;
}

.tool-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(1, 137, 143, 0.12);
}

.tool-btn svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.2;
  stroke-linecap: round;
  stroke-linejoin: round;
  flex: none;
}

.header-nav-row {
  height: 41px;
  display: grid;
  grid-template-columns: minmax(36px, calc((100vw - var(--site-container)) / 2)) 176px 1fr;
}

.nav-lead,
.main-nav {
  background: #01898f;
}

.brand {
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 14px;
  cursor: pointer;
  transition: background 0.24s ease, box-shadow 0.24s ease;
}

.brand:hover {
  background: #f8ffff;
  box-shadow: inset 0 -3px 0 rgba(1, 137, 143, 0.22);
}

.brand img {
  width: 150px;
  max-width: 100%;
  max-height: 38px;
  object-fit: contain;
  image-rendering: auto;
}

.main-nav {
  display: flex;
  align-items: center;
  gap: clamp(28px, 4vw, 54px);
  padding: 0 36px 0 72px;
  overflow: visible;
}

.nav-entry {
  position: relative;
  height: 100%;
  display: inline-flex;
  align-items: center;
}

.nav-item {
  border: 0;
  background: transparent;
  color: #eefcfc;
  font-size: 18px;
  line-height: 1;
  font-family: var(--font-nav);
  white-space: nowrap;
  cursor: pointer;
  padding: 0;
  position: relative;
  transition: color 0.22s ease;
}

.nav-submenu {
  position: absolute;
  top: 100%;
  left: 50%;
  z-index: 32;
  min-width: 196px;
  padding: 10px;
  border: 1px solid rgba(1, 137, 143, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 18px 42px rgba(17, 44, 48, 0.16);
  opacity: 0;
  pointer-events: none;
  transform: translate(-50%, 8px);
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.nav-entry:hover .nav-submenu,
.nav-entry:focus-within .nav-submenu {
  opacity: 1;
  pointer-events: auto;
  transform: translate(-50%, 0);
}

.nav-subitem {
  height: 38px;
  padding: 0 14px;
  border-radius: 6px;
  color: #0c5256;
  font-size: 15px;
  line-height: 38px;
  font-family: var(--font-nav);
  text-decoration: none;
  white-space: nowrap;
  display: block;
  transition: background 0.18s ease, color 0.18s ease;
}

.nav-subitem:hover,
.nav-subitem:focus {
  background: rgba(1, 137, 143, 0.1);
  color: #01898f;
}

.nav-item::after {
  content: "";
  position: absolute;
  left: 50%;
  right: 50%;
  bottom: -12px;
  height: 2px;
  background: #ffffff;
  border-radius: 999px;
  transition: left 0.22s ease, right 0.22s ease;
}

.nav-item:hover {
  color: #ffffff;
}

.nav-item.active {
  color: #ffffff;
}

.nav-item.active::after,
.nav-item:hover::after {
  left: 0;
  right: 0;
}

.mobile-actions,
.mobile-nav-mask {
  display: none;
}

@media (max-width: 1020px) {
  .site-header .wrapper {
    max-width: calc(100% - 48px);
  }

  .header-nav-row {
    grid-template-columns: 24px 160px 1fr;
  }

  .main-nav {
    gap: 26px;
    padding-left: 38px;
  }

  .nav-item {
    font-size: 16px;
  }
}

@media (max-width: 1024px) {
  .site-header {
    height: 64px;
  }

  .header-tools {
    display: none;
  }

  .header-nav-row {
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    box-sizing: border-box;
  }

  .nav-lead {
    display: none;
  }

  .brand {
    position: relative;
    z-index: 34;
    background: transparent;
    padding: 0 12px;
  }

  .brand img {
    width: 126px;
    max-height: 32px;
  }

  .main-nav {
    position: fixed;
    z-index: 33;
    top: 64px;
    right: 16px;
    left: 16px;
    display: grid;
    gap: 0;
    padding: 10px;
    overflow: hidden;
    border-radius: 16px;
    background: rgba(1, 137, 143, 0.96);
    box-shadow: 0 22px 52px rgba(17, 44, 48, 0.22);
    transform: translateY(-12px);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.22s ease, transform 0.22s ease;
  }

  .main-nav.open {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0);
  }

  .nav-entry {
    height: auto;
    display: block;
  }

  .nav-item {
    height: 46px;
    width: 100%;
    padding: 0 16px;
    font-size: 16px;
    text-align: left;
    border-radius: 10px;
  }

  .nav-item::after {
    display: none;
  }

  .nav-item:hover,
  .nav-item.active {
    background: rgba(255, 255, 255, 0.13);
  }

  .nav-submenu {
    position: static;
    min-width: 0;
    padding: 0 0 6px 18px;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    opacity: 1;
    pointer-events: auto;
    transform: none;
  }

  .nav-entry:hover .nav-submenu,
  .nav-entry:focus-within .nav-submenu {
    transform: none;
  }

  .nav-subitem {
    height: 34px;
    padding: 0 14px;
    border-radius: 9px;
    color: rgba(255, 255, 255, 0.86);
    font-size: 14px;
    line-height: 34px;
  }

  .nav-subitem:hover,
  .nav-subitem:focus {
    background: rgba(255, 255, 255, 0.13);
    color: #ffffff;
  }

  .mobile-actions {
    position: relative;
    z-index: 34;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .mobile-icon-btn,
  .mobile-menu-btn {
    width: 38px;
    height: 38px;
    border: 0;
    border-radius: 50%;
    background: rgba(1, 137, 143, 0.08);
    color: #01898f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .mobile-icon-btn svg {
    width: 21px;
    height: 21px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.35;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .mobile-menu-btn {
    flex-direction: column;
    gap: 4px;
  }

  .mobile-menu-btn span {
    width: 17px;
    height: 2px;
    border-radius: 999px;
    background: currentColor;
    transition: transform 0.2s ease, opacity 0.2s ease;
  }

  .site-header.is-menu-open .mobile-menu-btn span:nth-child(1) {
    transform: translateY(6px) rotate(45deg);
  }

  .site-header.is-menu-open .mobile-menu-btn span:nth-child(2) {
    opacity: 0;
  }

  .site-header.is-menu-open .mobile-menu-btn span:nth-child(3) {
    transform: translateY(-6px) rotate(-45deg);
  }

  .mobile-nav-mask {
    display: block;
    position: fixed;
    z-index: 31;
    inset: 64px 0 0;
    border: 0;
    background: rgba(9, 24, 28, 0.22);
    backdrop-filter: blur(2px);
    cursor: pointer;
  }

  .mobile-mask-fade-enter-active,
  .mobile-mask-fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .mobile-mask-fade-enter,
  .mobile-mask-fade-leave-to {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tool-btn {
    transition: none;
  }

  .tool-btn:hover,
  .tool-btn:focus-visible,
  .tool-btn:active {
    transform: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/layout/header/index.vue